<script context="module" lang="ts">
  export type SelectEvent = {
    region: string;
    countryCode: string;
    lang: string;
    defaultLanguage: string;
  };
</script>

<script lang="ts">
  import { assets } from "$app/paths";
  import { _ } from "svelte-i18n";
  import { createEventDispatcher } from "svelte";
  import { regionStore } from "$lib/stores/region";
  import cfg from "$lib/config/config";
  import { getCountryWithArticle } from "$lib/data/countries.data";
  import {
    getCountryRegion,
    getCountry,
    getLanguages,
  } from "$lib/data/regionSelector.data";
  import type { CountryData, LangData } from "$lib/data/regionSelector.data";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Button from "$lib/components/Button/Button.svelte";
  import Modal from "$lib/components/Modal/Modal.svelte";
  import RegionSelectorDropdowns from "../RegionSelectorDropdowns/RegionSelectorDropdowns.svelte";

  const regionInfo = regionStore();

  export let modal: Modal = undefined;
  export let countryCode = "gb";
  export let activeRegion: string = cfg.REGION_ID;
  export let suggestedRegion: string = cfg.REGION_ID;
  export let preferences: {
    region?: string;
    country?: string;
    locale?: string;
  } = undefined;
  export let isOpen = false;
  let unsupportedRegion: boolean = suggestedRegion === "none";

  /** Recommended region mapped from country code */
  const region = getCountryRegion(countryCode);
  const countryName = getCountryWithArticle(countryCode);
  const countryData = getCountry(countryCode);
  const lang = (preferences && preferences.locale) || $regionInfo.locale;
  const langFromCountry = (country: CountryData) =>
    country.languages?.includes(lang) ? lang : country.defaultLanguage;
  const labelledLang = getLanguages(lang);
  const dispatch = createEventDispatcher<{
    confirm: SelectEvent;
    continue: SelectEvent;
  }>();
  let selectedCountry: CountryData;
  let selectedLanguage: LangData;
  $: selectedCountry = countryData;
  $: selectedRegion =
    selectedCountry && getCountryRegion(selectedCountry.countryCode).regionCode;
  $: selectedLanguage = labelledLang;

  $: if (selectedCountry && selectedCountry.defaultLanguage !== undefined) {
    selectedLanguage = getLanguages(selectedCountry.defaultLanguage);
  }

  function close(event: any) {
    if (event.detail.noContinue) {
      return;
    }
    dispatch("continue", {
      region: activeRegion,
      countryCode,
      lang: $regionInfo.locale,
      defaultLanguage: countryData && countryData.defaultLanguage,
    });
  }
</script>

<style lang="scss">
  .region-modal {
    background-color: $surface;
    display: grid;
    row-gap: 24px;
    justify-items: center;
    margin: 0 16px;
    padding: 32px 24px 48px;
    text-align: center;
    @include breakpoint(md) {
      width: 376px;
    }

    img {
      width: 100%;
      max-width: 107px;
    }

    button {
      background-color: transparent;
      border: none;
    }
  }
</style>

<Modal
  bind:this={modal}
  on:close={close}
  backgroundColor="var(--on-surface-30)"
  {isOpen}
>
  <div class="region-modal">
    <img 
      class="logo" 
      src={`${assets}/logo.svg`}
      alt="Belstaff Logo"
      width="107"
      height="60.43"
    />
    {#if unsupportedRegion}
      <div class="region-modal--message">
        <Typography component="p" variant="body1"
          >{$_("Sorry, we do not currently ship to")}
          <b>{countryName}</b>. {$_(
            "Please choose an alternative delivery region from the list below",
          )}</Typography
        >
      </div>
      <RegionSelectorDropdowns
        {selectedCountry}
        {selectedLanguage}
        on:selectedCountry={e => {
          selectedCountry = e.detail;
          if (
            selectedCountry &&
            selectedCountry.languages?.includes(selectedLanguage.code) === false
          ) {
            selectedLanguage = undefined;
          }
        }}
        on:selectedLanguage={e => {
          selectedLanguage = e.detail;
        }}
      />
      <Button
        label={$_("Save preferences")}
        disabled={selectedCountry === undefined ||
          selectedLanguage === undefined}
        primary
        on:click={() => {
          dispatch("confirm", {
            region: selectedRegion || (region && region.regionCode),
            countryCode:
              (selectedCountry && selectedCountry.countryCode) || countryCode,
            lang:
              (selectedLanguage && selectedLanguage.code) ||
              langFromCountry(countryData),
            defaultLanguage: selectedCountry
              ? selectedCountry.defaultLanguage
              : countryData && countryData.defaultLanguage,
          });
          modal.close({ noContinue: true });
        }}
      />
    {:else}
      {#if preferences && !preferences.region}
        <Typography
          >{$_("Welcome to Belstaff!")} <br />
          {$_("Please confirm your preferences below")}</Typography
        >
      {/if}

      <RegionSelectorDropdowns
        {selectedCountry}
        {selectedLanguage}
        on:selectedCountry={e => {
          selectedCountry = e.detail;
          if (
            selectedCountry &&
            selectedCountry.languages.includes(selectedLanguage.code) === false
          ) {
            selectedLanguage = undefined;
          }
        }}
        on:selectedLanguage={e => {
          selectedLanguage = e.detail;
        }}
      />

      <Button
        label={$_("Save preferences")}
        disabled={selectedCountry === undefined ||
          selectedLanguage === undefined}
        primary
        on:click={() => {
          dispatch("confirm", {
            region: selectedRegion || (region && region.regionCode),
            countryCode:
              (selectedCountry && selectedCountry.countryCode) || countryCode,
            lang:
              (selectedLanguage && selectedLanguage.code) ||
              langFromCountry(countryData),
            defaultLanguage: selectedCountry
              ? selectedCountry.defaultLanguage
              : countryData && countryData.defaultLanguage,
          });
          modal.close({ noContinue: true });
        }}
      />
    {/if}
    <button
      on:click={() => {
        dispatch("continue", {
          region: activeRegion,
          countryCode,
          lang: $regionInfo.locale,
          defaultLanguage: countryData && countryData.defaultLanguage,
        });
        modal.close({ noContinue: true });
      }}
    >
      <Typography component="p" variant="body1" underline
        >{$_("Don't update preferences")}</Typography
      >
    </button>
  </div>
</Modal>
